// src/services/permissionService.js
import { supabase } from './supabaseClient';

export const permissionService = {
  /**
   * Check if user has a specific permission
   * @param {string} userId - User's UUID
   * @param {string} permissionCode - Permission code (e.g., 'can_create_kpi_system')
   * @returns {Promise<boolean>}
   */
  async hasPermission(userId, permissionCode) {
    try {
      // Get user's role
      const { data: user, error: userError } = await supabase
        .from('users')
        .select('role_id')
        .eq('id', userId)
        .single();

      if (userError || !user) {
        console.error('Error fetching user role:', userError);
        return false;
      }

      // Get role's permissions
      const { data: permissions, error: permError } = await supabase
        .from('role_permissions')
        .select('permissions(code)')
        .eq('role_id', user.role_id);

      if (permError) {
        console.error('Error fetching role permissions:', permError);
        return false;
      }

      // Check if permission code exists
      return permissions.some(rp => rp.permissions.code === permissionCode);
    } catch (err) {
      console.error('Permission check failed:', err);
      return false;
    }
  },

  /**
   * Get all permissions for a user (based on their role)
   * @param {string} userId - User's UUID
   * @returns {Promise<Array<string>>} Array of permission codes
   */
  async getUserPermissions(userId) {
    try {
      // Get user's role
      const { data: user, error: userError } = await supabase
        .from('users')
        .select('role_id')
        .eq('id', userId)
        .single();

      if (userError || !user) {
        console.error('Error fetching user role:', userError);
        return [];
      }

      // Get all permissions for user's role
      const { data: permissions, error: permError } = await supabase
        .from('role_permissions')
        .select('permissions(code)')
        .eq('role_id', user.role_id);

      if (permError) {
        console.error('Error fetching role permissions:', permError);
        return [];
      }

      return permissions.map(rp => rp.permissions.code);
    } catch (err) {
      console.error('Error getting user permissions:', err);
      return [];
    }
  },

  /**
   * Check if supervisor can supervise supervisee
   * @param {string} supervisorId - Supervisor's UUID
   * @param {string} superviseeId - Supervisee's UUID
   * @returns {Promise<boolean>}
   */
  async canSupervise(supervisorId, superviseeId) {
    try {
      const { data, error } = await supabase
        .from('supervisor_assignments')
        .select('id')
        .eq('supervisor_id', supervisorId)
        .eq('supervisee_id', superviseeId)
        .single();

      if (error && error.code === 'PGRST116') {
        // No row found
        return false;
      }

      if (error) {
        console.error('Error checking supervision:', error);
        return false;
      }

      return !!data;
    } catch (err) {
      console.error('Error in canSupervise:', err);
      return false;
    }
  },

  /**
   * Get all supervisees for a supervisor
   * @param {string} supervisorId - Supervisor's UUID
   * @returns {Promise<Array<object>>} Array of supervisees with id, name, can_edit_submissions
   */
  async getSupervisees(supervisorId) {
    try {
      const { data, error } = await supabase
        .from('supervisor_assignments')
        .select('supervisee_id, can_edit_submissions, users!supervisor_assignments_supervisee_id_fkey(id, name, email, team_lead_id)')
        .eq('supervisor_id', supervisorId);

      if (error) {
        console.error('Error fetching supervisees:', error);
        return [];
      }

      return data.map(sa => ({
        id: sa.supervisee_id,
        name: sa.users.name,
        email: sa.users.email,
        canEdit: sa.can_edit_submissions,
        teamLeadId: sa.users.team_lead_id ?? null,
      }));
    } catch (err) {
      console.error('Error in getSupervisees:', err);
      return [];
    }
  },

  /**
   * Get all supervisors for a user
   * @param {string} userId - User's UUID
   * @returns {Promise<Array<object>>} Array of supervisors with id, name, email
   */
  async getSupervisors(userId) {
    try {
      const { data, error } = await supabase
        .from('supervisor_assignments')
        .select('supervisor_id, users!supervisor_assignments_supervisor_id_fkey(id, name, email)')
        .eq('supervisee_id', userId);

      if (error) {
        console.error('Error fetching supervisors:', error);
        return [];
      }

      return data.map(sa => ({
        id: sa.supervisor_id,
        name: sa.users.name,
        email: sa.users.email,
      }));
    } catch (err) {
      console.error('Error in getSupervisors:', err);
      return [];
    }
  },

  /**
   * Check if supervisor can edit supervisee's submissions
   * Must have 'can_edit_supervisee_submissions' permission AND edit enabled for this supervisee
   * @param {string} supervisorId - Supervisor's UUID
   * @param {string} superviseeId - Supervisee's UUID
   * @returns {Promise<boolean>}
   */
  async canEditSubmissions(supervisorId, superviseeId) {
    try {
      // Check if user has the permission
      const hasPermission = await this.hasPermission(supervisorId, 'can_edit_supervisee_submissions');
      if (!hasPermission) return false;

      // Check if specific supervisee assignment has edit enabled
      const { data, error } = await supabase
        .from('supervisor_assignments')
        .select('can_edit_submissions')
        .eq('supervisor_id', supervisorId)
        .eq('supervisee_id', superviseeId)
        .single();

      if (error) return false;
      return data?.can_edit_submissions === true;
    } catch (err) {
      console.error('Error in canEditSubmissions:', err);
      return false;
    }
  },
};
